// FLAGS: identity, authorization
// The six-box TOTP entry: validates the second factor, then marks the browser session logged in and
// routes the operator by role. This is where a dashboard session becomes usable.
import React, { createRef, ReactElement, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@openup/shared/components/Button/Button";
import { useNavigate } from "react-router";
import {
  AuthenticatorTwoFactorCodeRequestMethod,
  BACKSPACE_KEY,
  ENTER_KEY,
} from "@src/constants";
import authenticationService from "@src/services/authentication.service";
import { OpenUpRoles } from "@src/services/OpenUpRoles";
import { useAuthentication } from "@src/components/wrappers/AuthenticationProvider";
import { withSnackbar } from "@src/components/SnackBarComponent";
import { USER_LOCKED_OUT_ERROR } from "@src/utils/constants";
import LockedAccountMessage from "@src/components/LockedAccountMessage";

export interface CodeInputProps {
  codeLength: number;
  moveToNextStep: () => void;
  snackbarShowMessage: (
    message: string | ReactElement,
    severity?: string,
    duration?: number,
  ) => void;
}

const CodeInput = ({
  codeLength,
  moveToNextStep,
  snackbarShowMessage,
}: CodeInputProps) => {
  const { t } = useTranslation();
  const [inputCode, setInputCode] = useState("");
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { setLoggedIn, hasRole } = useAuthentication();

  const onInput = (_inputCode: string) => {
    setInputCode(_inputCode);
  };

  const onFocus = () => {
    setError(false);
  };

  const inputElements: ReactElement[] = [];
  const inputRefList: RefObject<HTMLInputElement>[] = [];

  const moveToNextInputField = (currentInputIndex: number) => {
    if (currentInputIndex === codeLength - 1) return;
    inputRefList[currentInputIndex + 1].current?.focus();
  };

  const moveToPreviousInputField = (currentInputIndex: number) => {
    if (currentInputIndex === 0) return;
    inputRefList[currentInputIndex - 1].current?.focus();
  };

  // FLAGS: identity, authorization
  // `setLoggedIn()` is what opens the app, and the role test that decides whether to call it reads the
  // roles the API put in session storage moments earlier — it filters navigation, not access, and the
  // server policy on each endpoint still decides everything. A wrong-role operator is sent to the
  // request-access page without a session, so nothing here needs to hold a boundary.
  async function onSubmit() {
    try {
      await authenticationService.authenticateTwoFactorCode(
        inputCode,
        AuthenticatorTwoFactorCodeRequestMethod,
      );

      if (!hasRole(OpenUpRoles.Expert, OpenUpRoles.Support, OpenUpRoles.Host)) {
        moveToNextStep();
        return;
      }

      setLoggedIn();
      navigate(hasRole(OpenUpRoles.Expert) ? "/clients" : "/");
    } catch (err: unknown) {
      const errorData = (err as { response?: { data?: string } }).response
        ?.data;
      if (errorData === USER_LOCKED_OUT_ERROR) {
        setError(false);
        snackbarShowMessage(<LockedAccountMessage />, "error", 5000);
      } else {
        setError(true);
      }
    }
  }

  const getInputCode = () =>
    inputRefList
      .map((ref: RefObject<HTMLInputElement>) => ref.current?.value ?? "")
      .join("");

  const handleInput = (
    event: React.FormEvent<HTMLDivElement>,
    index: number,
  ) => {
    onInput(getInputCode());
    if ((event.nativeEvent as InputEvent).data === null) return;
    moveToNextInputField(index);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (
      event.key === BACKSPACE_KEY &&
      !(event.target as HTMLInputElement).value
    )
      moveToPreviousInputField(index);
    onInput(getInputCode());
    if (event.key === ENTER_KEY) onSubmit();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLFormElement>) => {
    e.nativeEvent.preventDefault();
    const codeFromClipboard = e.clipboardData.getData("Text");
    codeFromClipboard.split("").forEach((char, index) => {
      if (!inputRefList[index]) return;
      const ref = inputRefList[index];
      if (ref.current) ref.current.value = char;
    });

    const focusIndex =
      codeFromClipboard.length < codeLength
        ? codeFromClipboard.length
        : codeLength - 1;
    inputRefList[focusIndex].current?.focus();
  };

  for (let i = 0; i < codeLength; i += 1) {
    const inputRef = createRef<HTMLInputElement>();

    inputRefList.push(inputRef);
    inputElements.push(
      <input
        autoFocus={i === 0}
        onKeyDown={(e) => handleKeyDown(e, i)}
        onInput={(e) => handleInput(e, i)}
        onFocus={onFocus}
        type="text"
        maxLength={1}
        className={`text-center w-10 border-b-2 border-t-0 border-l-0 border-r-0 outline-none text-base ${error ? "border-[#FF0000]" : "border-gray-400 focus:border-indigo-800"}`}
        ref={inputRef}
        key={i}
      />,
    );
  }

  return (
    <form onPaste={onPaste}>
      <div className="flex justify-center gap-4 mt-6 mb-6">{inputElements}</div>
      {error && (
        <p className="flex justify-center items-center text-xs text-[#FF0000]">
          {t("LoginView.GoogleAuthenticatorPage.Error")}
        </p>
      )}
      <div className="flex justify-center mt-6">
        <Button
          variant="primary"
          size="medium"
          className="w-[360px]"
          onClick={() => onSubmit()}
          disabled={inputCode.length !== codeLength}
        >
          <span className="text-sm font-medium tracking-widest">
            {t("LoginView.GoogleAuthenticatorPage.ButtonText")}
          </span>
        </Button>
      </div>
    </form>
  );
};

export default withSnackbar(CodeInput);
